/**
 * Keep Home / My Rides page caches coherent when a local draft syncs to Firestore.
 * Without this, the pending row vanishes (queue+draft cleared) before getDocs returns the new doc.
 */

import { cacheKey, getCachedStale, setCached } from './pageCache';
import { mergeFirestoreRunsWithPending, pendingRunSortMillis } from './pendingRunsQueue';
import { setMyRidesCacheWithFullList } from './myRunsFullListCache';
import { removeOfflineRunDetailCache } from './offlineRunDetailCache';
import { removeRunTelemetrySnapshot } from './runTelemetrySnapshot';

export const RUN_CACHE_PATCHED_EVENT = 'shokiq:run-cache-patched';

function sortRunsDesc(runs) {
  return [...(runs || [])].sort(
    (a, b) =>
      pendingRunSortMillis(b.timestamp || b.date || b.createdAt) -
      pendingRunSortMillis(a.timestamp || a.date || a.createdAt),
  );
}

/**
 * Strip large telemetry arrays from a run before writing to localStorage page cache.
 */
export function slimRunForListCache(run) {
  if (!run || typeof run !== 'object') return run;
  const tel = run.telemetryData;
  const n = Array.isArray(tel) ? tel.length : 0;
  const { telemetryData, ...rest } = run;
  return {
    ...rest,
    telemetryData: [],
    hasTelemetryData: n > 0,
  };
}

/**
 * Remove one run id from My Rides + Home list caches so the next fetch does not resurrect it via
 * salvageCachedRunsNotInFetch, and other listeners stay coherent.
 * @returns {boolean} true if any cache entry was updated
 */
export function removeRunIdFromUserRunListCaches(userId, runId) {
  if (!userId || !runId) return false;
  let wrote = false;

  const myKey = cacheKey('myRides', userId);
  const myCached = getCachedStale(myKey);
  if (myCached && Array.isArray(myCached.runs)) {
    const nextRuns = myCached.runs.filter((r) => r?.id !== runId);
    if (nextRuns.length !== myCached.runs.length) {
      setMyRidesCacheWithFullList(userId, { ...myCached, runs: nextRuns });
      wrote = true;
    }
  }

  const homeKey = cacheKey('home', userId);
  const homeCached = getCachedStale(homeKey);
  if (homeCached?.stats && Array.isArray(homeCached.stats.recentRides)) {
    const nextRecent = homeCached.stats.recentRides.filter((r) => r?.id !== runId);
    if (nextRecent.length !== homeCached.stats.recentRides.length) {
      setCached(homeKey, {
        ...homeCached,
        stats: {
          ...homeCached.stats,
          recentRides: nextRecent,
        },
      });
      wrote = true;
    }
  }

  if (wrote) {
    try {
      window.dispatchEvent(new CustomEvent(RUN_CACHE_PATCHED_EVENT, { detail: { userId } }));
    } catch {
      // ignore
    }
  }
  removeOfflineRunDetailCache(userId, runId).catch(() => {});
  removeRunTelemetrySnapshot(userId, runId).catch(() => {});
  return wrote;
}

/**
 * Put a run back into My Rides + Home page caches after an optimistic delete failed (e.g. deleteDoc error).
 * Uses a slim copy so large telemetry is not written to localStorage.
 */
export function restoreRunToUserRunListCaches(userId, run) {
  if (!userId || !run?.id) return;
  const card = slimRunForListCache(run);

  const myKey = cacheKey('myRides', userId);
  const myCached = getCachedStale(myKey);
  if (myCached && Array.isArray(myCached.runs)) {
    if (!myCached.runs.some((r) => r?.id === card.id)) {
      const merged = mergeFirestoreRunsWithPending(myCached.runs, [card]);
      setMyRidesCacheWithFullList(userId, { ...myCached, runs: merged });
    }
  }

  const homeKey = cacheKey('home', userId);
  const homeCached = getCachedStale(homeKey);
  if (homeCached?.stats && Array.isArray(homeCached.stats.recentRides)) {
    const withoutDup = homeCached.stats.recentRides.filter((r) => r?.id !== card.id);
    const mergedRecent = sortRunsDesc(mergeFirestoreRunsWithPending(withoutDup, [card])).slice(0, 5);
    setCached(homeKey, {
      ...homeCached,
      stats: {
        ...homeCached.stats,
        recentRides: mergedRecent,
      },
    });
  }

  try {
    window.dispatchEvent(new CustomEvent(RUN_CACHE_PATCHED_EVENT, { detail: { userId } }));
  } catch {
    // ignore
  }
}

/**
 * Firestore queries can briefly omit a doc right after addDoc. Re-attach any cloud-saved
 * rows from stale cache that are missing from the fresh merge so lists don't flicker empty.
 */
export function salvageCachedRunsNotInFetch(prevRuns, mergedRuns) {
  if (!Array.isArray(prevRuns) || prevRuns.length === 0) return mergedRuns;
  if (!Array.isArray(mergedRuns)) return mergedRuns;
  const ids = new Set(mergedRuns.map((r) => r?.id).filter(Boolean));
  const extras = prevRuns.filter((r) => {
    if (!r?.id || ids.has(r.id)) return false;
    if (r.pendingSync || r.id.startsWith('local-') || r.id.startsWith('queued-inline-')) return false;
    return true;
  });
  if (extras.length === 0) return mergedRuns;
  return mergeFirestoreRunsWithPending(mergedRuns, extras);
}

/**
 * Swap local-{draftId} for the real Firestore id in cached lists (stale-while-revalidate).
 */
export function patchCachesAfterPendingRunSynced(userId, localDraftId, firestoreId, runForListUi) {
  if (!userId || !localDraftId || !firestoreId || !runForListUi) return;
  const localCardId = `local-${localDraftId}`;
  const card = slimRunForListCache({
    ...runForListUi,
    id: firestoreId,
    pendingSync: false,
  });

  // Always write My Rides + Home caches (default empty lists). Pending rows often exist only in
  // React state until merged; if disk cache never had `local-*`, the old guard skipped the patch
  // and the card vanished until the next full fetch.
  const myKey = cacheKey('myRides', userId);
  const myPrev = getCachedStale(myKey) || {};
  const baseRuns = Array.isArray(myPrev.runs) ? myPrev.runs : [];
  const withoutMy = baseRuns.filter((r) => r?.id !== localCardId && r?.id !== firestoreId);
  const mergedRuns = mergeFirestoreRunsWithPending(withoutMy, [card]);
  setMyRidesCacheWithFullList(userId, { ...myPrev, runs: mergedRuns });

  const homeKey = cacheKey('home', userId);
  const homePrev = getCachedStale(homeKey) || {};
  const prevStats = homePrev.stats && typeof homePrev.stats === 'object' ? homePrev.stats : {};
  const baseRecent = Array.isArray(prevStats.recentRides) ? prevStats.recentRides : [];
  const withoutHome = baseRecent.filter((r) => r?.id !== localCardId && r?.id !== firestoreId);
  const mergedRecent = sortRunsDesc(mergeFirestoreRunsWithPending(withoutHome, [card])).slice(0, 5);
  setCached(homeKey, {
    ...homePrev,
    stats: {
      totalRides: typeof prevStats.totalRides === 'number' ? prevStats.totalRides : mergedRecent.length,
      totalDistance: prevStats.totalDistance != null ? prevStats.totalDistance : '0.00',
      ...prevStats,
      recentRides: mergedRecent,
    },
  });

  try {
    window.dispatchEvent(new CustomEvent(RUN_CACHE_PATCHED_EVENT, { detail: { userId } }));
  } catch {
    // ignore
  }
}

/**
 * Update runName / name on a cached list card (My Rides + Home recent) after a rename.
 * @param {string} userId
 * @param {string} runId
 * @param {{ runName: string, name?: string }} fields
 * @returns {boolean} true if any cache entry was updated
 */
export function patchRunMetadataInUserRunListCaches(userId, runId, { runName, name }) {
  if (!userId || !runId) return false;
  const nm =
    typeof runName === 'string' && runName.trim()
      ? runName.trim()
      : typeof name === 'string'
        ? name.trim()
        : '';
  if (!nm) return false;
  const displayName = typeof name === 'string' && name.trim() ? name.trim() : nm;
  let wrote = false;

  const apply = (r) => {
    if (r?.id !== runId) return r;
    wrote = true;
    return { ...r, runName: nm, name: displayName };
  };

  const myKey = cacheKey('myRides', userId);
  const myCached = getCachedStale(myKey);
  if (myCached && Array.isArray(myCached.runs)) {
    const nextRuns = myCached.runs.map(apply);
    if (wrote) {
      setMyRidesCacheWithFullList(userId, { ...myCached, runs: nextRuns });
    }
  }

  let homeWrote = false;
  const homeKey = cacheKey('home', userId);
  const homeCached = getCachedStale(homeKey);
  if (homeCached?.stats && Array.isArray(homeCached.stats.recentRides)) {
    const before = homeCached.stats.recentRides;
    const nextRecent = before.map((r) => {
      if (r?.id !== runId) return r;
      homeWrote = true;
      return { ...r, runName: nm, name: displayName };
    });
    if (homeWrote) {
      setCached(homeKey, {
        ...homeCached,
        stats: {
          ...homeCached.stats,
          recentRides: nextRecent,
        },
      });
    }
  }

  if (wrote || homeWrote) {
    try {
      window.dispatchEvent(new CustomEvent(RUN_CACHE_PATCHED_EVENT, { detail: { userId } }));
    } catch {
      // ignore
    }
  }
  return wrote || homeWrote;
}
